import moment from 'moment-timezone';

const DATE_FORMAT = 'YYYY-MM-DD';
const TIME_FORMAT = 'HH:mm:ss';
const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const SEARCH_HORIZON_DAYS = 365;
const DISTANT_CUT_OFF_TYPES = ['EARLY_BIRD', 'MULTI_NIGHT'];
const LAST_MINUTE_TYPE = 'LAST_MINUTE_DEAL';
// the API suffixes Indonesian fields with "ind" while the app language code is "id"
const NAME_SUFFIX_BY_LANGUAGE = { id: 'ind' };

function getMerchantNow(now, timezone) {
  return timezone ? moment.tz(now, timezone) : moment(now);
}

// calendar day without a timezone, so night-by-night math never shifts across midnight
function toCalendarDay(date) {
  return moment(moment(date).format(DATE_FORMAT), DATE_FORMAT);
}

function isDateInRanges(date, ranges) {
  const dateText = date.format(DATE_FORMAT);
  return (ranges || []).some((range) => dateText >= range.start_date && dateText <= range.end_date);
}

function isDayAllowed(promotion, prefix, date) {
  if (!promotion[`is_use_custom_${prefix}_days`]) return true;
  return promotion[`${prefix}_${DAY_KEYS[date.day()]}`] === true;
}

function isStayNightValid(promotion, night) {
  const stayDate = promotion.stay_date || [];
  if (stayDate.length > 0 && !isDateInRanges(night, stayDate)) return false;
  if (isDateInRanges(night, promotion.blackout_stay_date)) return false;
  return isDayAllowed(promotion, 'stay', night);
}

// an invalid night only loses the discount, so one promo night keeps the stay valid (same as BE)
function isStayValid(promotion, checkIn, nights) {
  for (let index = 0; index < nights; index++) {
    if (isStayNightValid(promotion, checkIn.clone().add(index, 'd'))) return true;
  }
  return false;
}

function isCheckInWithinCutOff(promotion, checkIn, { merchantNow, timezone, checkInTime }) {
  if (promotion.promotion_type !== LAST_MINUTE_TYPE || !checkInTime) return true;

  const checkInText = `${checkIn.format(DATE_FORMAT)} ${checkInTime}`;
  const format = `${DATE_FORMAT} ${TIME_FORMAT}`;
  const checkInMoment = timezone ? moment.tz(checkInText, format, timezone) : moment(checkInText, format);
  return checkInMoment.subtract(promotion.cut_off || 0, 'hours').isSameOrBefore(merchantNow);
}

function getEarliestCheckIn(promotion, today) {
  if (!DISTANT_CUT_OFF_TYPES.includes(promotion.promotion_type)) return today.clone();
  return today.clone().add(promotion.cut_off || 0, 'd');
}

function getSearchHorizon(promotion, earliestCheckIn) {
  const stayEndDates = (promotion.stay_date || []).map((range) => range.end_date).sort();
  if (stayEndDates.length === 0) return earliestCheckIn.clone().add(SEARCH_HORIZON_DAYS, 'd');
  return moment(stayEndDates[stayEndDates.length - 1], DATE_FORMAT);
}

function findValidCheckIn(start, end, isCheckInValid) {
  for (const date = start.clone(); !date.isAfter(end); date.add(1, 'd')) {
    if (isCheckInValid(date)) return date.clone();
  }
  return null;
}

/**
 * Finds why a promotion cannot be offered today, for rules no search filter can satisfy.
 * @param {Object} promotion promotion-detail response data
 * @param {Object} context { now, timezone }
 * @returns {String} 'inactive' | 'sale_period' | 'quota', or '' when the promotion is usable
 */
export function getPromotionUnavailableReason(promotion, { now, timezone }) {
  if (!promotion.is_active) return 'inactive';

  const today = toCalendarDay(getMerchantNow(now, timezone).format(DATE_FORMAT));
  const saleDate = promotion.sale_date || [];
  const isOutsideSalePeriod =
    (saleDate.length > 0 && !isDateInRanges(today, saleDate)) ||
    isDateInRanges(today, promotion.blackout_sale_date) ||
    !isDayAllowed(promotion, 'sale', today);
  if (isOutsideSalePeriod) return 'sale_period';

  if (promotion.used >= promotion.max_use) return 'quota';
  return '';
}

/**
 * Moves the stay dates the guest searched with onto the nearest stay the promotion accepts.
 * @param {Object} promotion promotion-detail response data
 * @param {Object} context { checkIn, checkOut, now, timezone, checkInTime }
 * @returns {Object|null} { check_in, check_out }, or null when no stay qualifies
 */
export function resolvePromotionStayDate(promotion, { checkIn, checkOut, now, timezone, checkInTime }) {
  const merchantNow = getMerchantNow(now, timezone);
  const today = toCalendarDay(merchantNow.format(DATE_FORMAT));
  const currentCheckIn = toCalendarDay(checkIn);
  const currentNights = toCalendarDay(checkOut).diff(currentCheckIn, 'd');
  const nights = Math.max(currentNights, promotion.min_night || 1, 1);
  const earliestCheckIn = getEarliestCheckIn(promotion, today);
  const cutOffContext = { merchantNow, timezone, checkInTime };

  const isCheckInValid = (date) =>
    !date.isBefore(earliestCheckIn) &&
    isCheckInWithinCutOff(promotion, date, cutOffContext) &&
    isStayValid(promotion, date, nights);
  const toStayDate = (date) => ({
    check_in: date.toDate(),
    check_out: date.clone().add(nights, 'd').toDate(),
  });

  if (isCheckInValid(currentCheckIn)) return toStayDate(currentCheckIn);

  // the guest's own dates come first, so look forward from them before falling back to earlier days
  const horizon = getSearchHorizon(promotion, earliestCheckIn);
  const searchStart = moment.max(currentCheckIn, earliestCheckIn);
  const nearestCheckIn =
    findValidCheckIn(searchStart, horizon, isCheckInValid) ||
    findValidCheckIn(earliestCheckIn, searchStart, isCheckInValid);
  if (nearestCheckIn) return toStayDate(nearestCheckIn);

  // a last-minute window that opens later today still makes today the stay to show
  const isLastMinute = promotion.promotion_type === LAST_MINUTE_TYPE;
  if (isLastMinute && isStayValid(promotion, today, nights)) return toStayDate(today);
  return null;
}

/**
 * Keeps only the name fields so a stored promotion can follow later language switches.
 * @param {Object} promotion promotion-detail response data
 * @returns {Object} name and name_<language> fields
 */
export function pickPromotionNames(promotion) {
  return Object.keys(promotion)
    .filter((key) => key === 'name' || key.indexOf('name_') === 0)
    .reduce((names, key) => ({ ...names, [key]: promotion[key] }), {});
}

/**
 * Picks the promotion name in the active language.
 * @param {Object} promotion promotion-detail response data
 * @param {String} languageCode app language code
 * @returns {String} localized name, falling back to the default name
 */
export function getPromotionName(promotion, languageCode) {
  const suffix = NAME_SUFFIX_BY_LANGUAGE[languageCode] || languageCode;
  return promotion[`name_${suffix}`] || promotion.name || '';
}



// WEBPACK FOOTER //
// ./src/utils/bePromotionRule.js